/**
 * Link Preview Utility
 * Mirrors the mobile app's fetchLinkPreview from property.service.ts:
 *  1. Parse address from URL for known real estate sites (instant)
 *  2. Fetch OG meta tags via CORS proxy (for real image, title, description)
 *  3. Merge — OG data takes priority, URL-parsed is fallback
 */

const CORS_PROXY = 'https://api.allorigins.win/get?url=';

// Known real estate domains — block bot scrapers, so skip proxy fetch for them
const REAL_ESTATE_DOMAIN_RE = /zillow\.com|redfin\.com|realtor\.com|trulia\.com|homes\.com/i;

// ── OG tag helpers ────────────────────────────────────────────────────────────

const getOgTag = (html, property) => {
  const regex = new RegExp(
    `<meta[^>]*property=["']${property}["'][^>]*content=["']([^"']*)["']` +
    `|<meta[^>]*content=["']([^"']*)["'][^>]*property=["']${property}["']`,
    'i'
  );
  const match = html.match(regex);
  const value = match?.[1] || match?.[2] || null;
  return value && value.trim() ? value.trim() : null;
};

const resolveUrl = (base, relative) => {
  if (!relative) return null;
  if (relative.startsWith('http://') || relative.startsWith('https://')) return relative;
  const originMatch = base.match(/^(https?:\/\/[^/?#]+)/i);
  if (originMatch) {
    return relative.startsWith('/')
      ? `${originMatch[1]}${relative}`
      : `${originMatch[1]}/${relative}`;
  }
  return relative;
};

// ── Rialty property link detector ─────────────────────────────────────────────
// Mirrors mobile app's parseRialtyPropertyUrl in property.service.ts. Matches
// apex + www + dev/stg subdomains, e.g.
//   https://rialty.ai/property/1159287939
//   https://dev.rialty.ai/property/1159287939
//   https://stg.rialty.ai/property/1159287939
// Tolerant of a doubled slash before "property" (e.g. dev.rialty.ai//property/…),
// a real-world variant mobile's stricter regex doesn't match — `\/+` here is a
// strict superset, so every URL mobile recognizes is still recognized.
// Returns the mlsId string, or null for anything else.

export const parseRialtyPropertyUrl = (url) => {
  const match = url.match(/^https?:\/\/(?:www\.|dev\.|stg\.)?rialty\.ai\/+property\/(\d+)/i);
  return match ? match[1] : null;
};

// ── Structured address parser for ATTOM API lookup ───────────────────────────
// Mirrors mobile app's parseStructuredAddress in property.service.ts

export const parseStructuredAddress = (url) => {
  try {
    const urlMatch = url.match(/^https?:\/\/([^/?#]+)(\/[^?#]*)?/i);
    if (!urlMatch) return null;

    const hostname = urlMatch[1];
    const pathname = urlMatch[2] || '/';

    // Zillow: /homedetails/126-Knightsbridge-Park-Ln-Houston-TX-77018/337097114_zpid/
    if (/zillow\.com/i.test(hostname)) {
      const match = pathname.match(/\/homedetails\/([^/]+)\//);
      if (!match) return null;
      const slug = match[1];
      // Remove zpid suffix if present (e.g., "28435028_zpid")
      const cleanSlug = slug.replace(/\/?\d+_zpid$/, '');
      const stateZipMatch = cleanSlug.match(/^(.+)-([A-Z]{2})-(\d{5}(?:-\d{4})?)$/);
      if (!stateZipMatch) return null;
      // Split address part into street and city — Zillow slugs are one blob
      // ("Street-Name-City-Name"), so find where the city starts by looking
      // for the last common street suffix (Ave, Ln, Blvd, ...) or unit prefix.
      const parts = stateZipMatch[1].split('-');
      const streetSuffixes = /^(St|Ave|Blvd|Dr|Ln|Ct|Rd|Way|Pl|Cir|Ter|Trl|Pkwy|Hwy|Loop|Run|Pass|Pt|Cv|Xing|Park|Court|Circle|Drive|Lane|Place|Trail|Road|Street|Avenue|Boulevard)$/i;
      const unitPrefixes = /^(Unit|Apt|Ste|Fl|Suite|Bldg)$/i;
      let splitIdx = -1;
      for (let i = 0; i < parts.length; i++) {
        if (streetSuffixes.test(parts[i])) {
          splitIdx = i;
        } else if (unitPrefixes.test(parts[i])) {
          // Include unit number (e.g., "Unit-703", "Apt-5A" → keep both as street)
          splitIdx = (i + 1 < parts.length && /^[\dA-Z]+$/i.test(parts[i + 1])) ? i + 1 : i;
        }
      }
      if (splitIdx >= 0 && splitIdx < parts.length - 1) {
        const street = parts.slice(0, splitIdx + 1).join(' ');
        const city = parts.slice(splitIdx + 1).join(' ');
        return { street, city, state: stateZipMatch[2], zip: stateZipMatch[3] };
      }
      // Fallback: return entire address part as street (no city extracted)
      const addressPart = stateZipMatch[1].replace(/-/g, ' ');
      return { street: addressPart, state: stateZipMatch[2], zip: stateZipMatch[3] };
    }

    // Redfin: /TX/Houston/126-Knightsbridge-Park-Ln-77018/home/48368134
    if (/redfin\.com/i.test(hostname)) {
      const match = pathname.match(/\/([A-Z]{2})\/([^/]+)\/([^/]+)\/home\//i);
      if (!match) return null;
      const state = match[1].toUpperCase();
      const city = match[2].replace(/-/g, ' ');
      const streetSlug = match[3];
      const zipMatch = streetSlug.match(/^(.+)-(\d{5})$/);
      if (!zipMatch) return null;
      return { street: zipMatch[1].replace(/-/g, ' '), city, state, zip: zipMatch[2] };
    }

    // Realtor.com: /realestateandhomes-detail/126-Knightsbridge-Park-Ln_Houston_TX_77018
    if (/realtor\.com/i.test(hostname)) {
      const match = pathname.match(/\/realestateandhomes-detail\/([^/?]+)/);
      if (!match) return null;
      const parts = match[1].split('_');
      if (parts.length < 3) return null;
      return {
        street: parts[0].replace(/-/g, ' '),
        city: parts[1].replace(/-/g, ' '),
        state: parts[2],
        zip: parts[3] || '',
      };
    }

    // Trulia: /home/126-knightsbridge-park-ln-houston-tx-77018-337097114
    if (/trulia\.com/i.test(hostname)) {
      const match = pathname.match(/\/home\/([^/]+)/i);
      if (!match) return null;
      const cleaned = match[1].replace(/-\d+$/, '');
      const stateZipMatch = cleaned.match(/^(.+)-([a-z]{2})-(\d{5})$/i);
      if (!stateZipMatch) return null;
      return { street: stateZipMatch[1].replace(/-/g, ' '), state: stateZipMatch[2].toUpperCase(), zip: stateZipMatch[3] };
    }

    // Homes.com: /property/126-knightsbridge-park-ln-houston-tx-77018/
    if (/homes\.com/i.test(hostname)) {
      const match = pathname.match(/\/property\/([^/]+)/i);
      if (!match) return null;
      const stateZipMatch = match[1].match(/^(.+)-([a-z]{2})-(\d{5})$/i);
      if (!stateZipMatch) return null;
      return { street: stateZipMatch[1].replace(/-/g, ' '), state: stateZipMatch[2].toUpperCase(), zip: stateZipMatch[3] };
    }

    return null;
  } catch {
    return null;
  }
};

// ── URL parser for known real estate sites (instant, no fetch) ────────────────

const parseRealEstateUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace(/^www\./, '');
    const pathname = urlObj.pathname;

    if (/zillow\.com/i.test(hostname)) {
      const match = pathname.match(/\/homedetails\/([^/]+)\//);
      if (match) {
        const slug = match[1];
        const m = slug.match(/^(.+)-([A-Z]{2})-(\d{5}(?:-\d{4})?)$/);
        if (m) {
          return {
            title: `${m[1].replace(/-/g, ' ')}, ${m[2]} ${m[3]}`,
            siteName: 'Zillow',
            description: 'View property on Zillow',
            imageUrl: null,
          };
        }
      }
      return { title: 'View on Zillow', siteName: 'Zillow', description: null, imageUrl: null };
    }

    if (/redfin\.com/i.test(hostname)) {
      const match = pathname.match(/\/[A-Z]{2}\/([^/]+)\/([^/]+)\/home\//i);
      if (match) {
        return {
          title: match[2].replace(/-/g, ' '),
          siteName: 'Redfin',
          description: 'View property on Redfin',
          imageUrl: null,
        };
      }
      return { title: 'View on Redfin', siteName: 'Redfin', description: null, imageUrl: null };
    }

    if (/realtor\.com/i.test(hostname)) {
      const match = pathname.match(/\/realestateandhomes-detail\/([^/?]+)/);
      if (match) {
        return {
          title: match[1].split('_')[0].replace(/-/g, ' '),
          siteName: 'Realtor.com',
          description: 'View property on Realtor.com',
          imageUrl: null,
        };
      }
      return { title: 'View on Realtor.com', siteName: 'Realtor.com', description: null, imageUrl: null };
    }

    if (/trulia\.com/i.test(hostname)) {
      return { title: 'View on Trulia', siteName: 'Trulia', description: null, imageUrl: null };
    }

    if (/homes\.com/i.test(hostname)) {
      return { title: 'View on Homes.com', siteName: 'Homes.com', description: null, imageUrl: null };
    }

    // Generic URL — use domain as title
    return { title: hostname, siteName: hostname, description: null, imageUrl: null };
  } catch {
    return null;
  }
};

// ── OG tag fetcher via CORS proxy ─────────────────────────────────────────────

const fetchOgTags = async (url) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    const proxyUrl = `${CORS_PROXY}${encodeURIComponent(url)}`;
    const response = await fetch(proxyUrl, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) return null;

    const json = await response.json();
    const html = json.contents;
    if (!html) return null;

    // Only parse the <head> section for performance
    const headEnd = html.indexOf('</head>');
    const relevantHtml = headEnd > 0 ? html.substring(0, headEnd + 7) : html.substring(0, 50000);

    const title =
      getOgTag(relevantHtml, 'og:title') ||
      relevantHtml.match(/<title[^>]*>([^<]+)<\/title>/i)?.[1]?.trim() ||
      null;

    const description =
      getOgTag(relevantHtml, 'og:description') ||
      relevantHtml.match(/<meta[^>]*name=["']description["'][^>]*content=["']([^"']*)["']/i)?.[1]?.trim() ||
      null;

    let imageUrl = getOgTag(relevantHtml, 'og:image');
    if (imageUrl) imageUrl = resolveUrl(url, imageUrl);

    const siteName = getOgTag(relevantHtml, 'og:site_name');
    // Capture the final URL after any redirects (e.g. TinyURL → redfin.com)
    const resolvedUrl = json?.status?.url || null;

    return { title, description, imageUrl: imageUrl || null, siteName, resolvedUrl };
  } catch {
    return null;
  }
};

// ── Short URL resolver ────────────────────────────────────────────────────────

/**
 * Resolve a short URL (e.g. redf.in/xxx) to its canonical destination URL.
 * Uses the same allorigins.win proxy already used in fetchOgTags so there is
 * no new third-party dependency.  Returns the original url on any failure so
 * callers can always proceed with the input they already have.
 *
 * @param {string} url
 * @returns {Promise<string>}
 */
export const resolveShortUrl = async (url) => {
  // Full real estate URLs don't need resolution
  if (REAL_ESTATE_DOMAIN_RE.test(url)) return url;
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);
    const proxyUrl = `${CORS_PROXY}${encodeURIComponent(url)}`;
    const response = await fetch(proxyUrl, { signal: controller.signal });
    clearTimeout(timeoutId);
    if (!response.ok) return url;
    const json = await response.json();
    // allorigins returns the final URL (after redirects) in status.url
    const finalUrl = json?.status?.url;
    return finalUrl && finalUrl !== url ? finalUrl : url;
  } catch {
    return url;
  }
};

// ── Main exported function ────────────────────────────────────────────────────

/**
 * Fetch link preview for a URL.
 * Mirrors mobile app's fetchLinkPreview in property.service.ts.
 *
 * @param {string} url
 * @returns {Promise<{url, title, description, imageUrl, siteName} | null>}
 */
export const fetchLinkPreview = async (url) => {
  const urlParsed = parseRealEstateUrl(url);

  // For known real estate sites, allorigins.win gets blocked by bot-detection
  // (causes "canceled" status in DevTools). URL parsing already gives us
  // title + siteName instantly — no proxy needed. Real property data
  // comes from ATTOM anyway.
  let ogData = null;
  let effectiveUrl = url;
  if (!REAL_ESTATE_DOMAIN_RE.test(url)) {
    ogData = await fetchOgTags(url);
    // allorigins follows HTTP redirects and exposes the final URL in status.url.
    // Use it as the canonical URL so TinyURL / redf.in / zillow.me links resolve
    // to their destination real estate site for domain checks and sourceUrl storage.
    const proxyResolved = ogData?.resolvedUrl;
    if (
      proxyResolved &&
      proxyResolved !== url &&
      !proxyResolved.includes('allorigins')
    ) {
      effectiveUrl = proxyResolved;
    }
  }

  // Re-parse with the canonical URL in case the original was a short link
  const urlParsedEffective =
    effectiveUrl !== url ? parseRealEstateUrl(effectiveUrl) : urlParsed;

  if (!urlParsed && !urlParsedEffective && !ogData) return null;

  const result = {
    // Use canonical URL so the link preview card and Path B domain checks work
    // correctly for TinyURL / redf.in / zillow.me short links.
    url: effectiveUrl,
    title: ogData?.title || urlParsedEffective?.title || urlParsed?.title || null,
    description: ogData?.description || urlParsedEffective?.description || urlParsed?.description || null,
    imageUrl: ogData?.imageUrl || urlParsedEffective?.imageUrl || urlParsed?.imageUrl || null,
    siteName: ogData?.siteName || urlParsedEffective?.siteName || urlParsed?.siteName || null,
  };

  // Must have at least a title to be useful
  return result.title ? result : null;
};
